/**
 * ProductionHeartbeat — a live ticker showing the last 24h of activity
 * across every studio (Music · News · Avatar · Podcasts · Imaging ·
 * Commercials · Social Posts) for the active station.
 *
 * Mimics a live broadcast monitor: 8s auto-advance rolling carousel of
 * recent events with a pulsing "on-air" dot, the time-ago, an icon
 * matching the event type, and a click-through to the matching studio.
 *
 * Polls `/api/control-room/heartbeat` every 45s (lightweight aggregate)
 * and resets on `station-switched` events so it always tracks the
 * currently active workspace.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Music, Newspaper, Mic2, Headphones, Volume2,
  Megaphone, Share2, Film, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { getStationTheme } from "@/lib/stationTheme";

const ICONS = {
  music:      Music,
  news:       Newspaper,
  news_video: Film,
  avatar:     Mic2,
  podcast:    Headphones,
  imaging:    Volume2,
  commercial: Megaphone,
  post:       Share2,
};

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "";
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ""; }
}

export default function ProductionHeartbeat() {
  const [theme, setTheme] = useState(getStationTheme(""));
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/stations/me").catch(() => null),
        api.get("/control-room/heartbeat"),
      ]);
      if (s?.data) setTheme(getStationTheme(s.data.genre || ""));
      const list = h?.data?.events || [];
      setEvents(list);
      if (list.length) setIdx((cur) => Math.min(cur, list.length - 1));
    } catch { /* keep last */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 8s auto-advance through events
  useEffect(() => {
    if (!events.length) return;
    const t = setInterval(() => {
      setIdx((cur) => (cur + 1) % events.length);
    }, 8000);
    return () => clearInterval(t);
  }, [events.length]);

  // 45s background poll for new activity
  useEffect(() => {
    pollRef.current = setInterval(() => { load(); }, 45000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Refresh whenever the user switches stations
  useEffect(() => {
    const onSwitch = () => load();
    window.addEventListener("station-switched", onSwitch);
    return () => window.removeEventListener("station-switched", onSwitch);
  }, [load]);

  if (!events.length) {
    return (
      <div
        className="mb-6 rounded-lg border bg-bg-surface/40 px-4 py-2.5 flex items-center gap-3"
        style={theme.borderStyle}
        data-testid="production-heartbeat-empty"
      >
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-ink-muted opacity-30" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-ink-muted" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
          Production Heartbeat
        </span>
        <span className="text-xs text-ink-muted">
          No activity in the last 24 hours — start producing!
        </span>
      </div>
    );
  }

  const e = events[idx] || events[0];
  const IconC = ICONS[e.kind] || Sparkles;

  return (
    <div
      className="mb-6 rounded-lg border bg-bg-surface/40 px-4 py-2.5 flex items-center gap-3 overflow-hidden"
      style={theme.borderStyle}
      data-testid="production-heartbeat"
    >
      {/* Pulsing on-air style dot — uses the station genre tint */}
      <span className="relative inline-flex h-2 w-2 shrink-0">
        <span
          className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
          style={{ backgroundColor: theme.hex }}
        />
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{ backgroundColor: theme.hex }}
        />
      </span>

      <span
        className="font-mono text-[10px] uppercase tracking-[0.18em] shrink-0"
        style={{ color: theme.hex }}
      >
        Production Heartbeat
      </span>

      {/* Rolling event card — clickable link to the source studio */}
      <Link
        to={e.target || "/dashboard"}
        className="flex-1 min-w-0 flex items-center gap-2.5 group"
        data-testid="production-heartbeat-event"
        key={e.created_at + idx}
      >
        <IconC className="h-3.5 w-3.5 shrink-0" style={{ color: theme.hex }} />
        <span className="text-xs text-ink-primary truncate">
          <Activity className="inline h-3 w-3 mr-1 align-text-bottom" style={{ color: theme.hex }} />
          <strong className="font-semibold">{e.label}</strong>
          <span className="text-ink-muted ml-1">— {e.detail}</span>
        </span>
        <span className="text-[10px] font-mono text-ink-muted shrink-0 ml-2">
          {timeAgo(e.created_at)}
        </span>
      </Link>

      {/* Compact counter showing position / total */}
      <span
        className="hidden sm:inline-flex font-mono text-[9px] uppercase tracking-wider text-ink-muted shrink-0"
        data-testid="production-heartbeat-count"
      >
        {idx + 1} / {events.length}
      </span>
    </div>
  );
}
